import React, { useState } from 'react';
import { Upload, Button, Card, Alert, Spin, Table, Tag, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { recognizeImage } from '@/services/ocr';
import type { MedicalExamIndicator } from '@/types';

const OCRUpload: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [indicators, setIndicators] = useState<MedicalExamIndicator[]>([]);
  const [rawText, setRawText] = useState('');

  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const result = await recognizeImage(file);
      setRawText(result.text);
      setIndicators(result.indicators);
      
      if (result.indicators.length > 0) {
        message.success(`识别出 ${result.indicators.length} 项指标`);
      } else {
        message.warning('未能识别出有效指标，请检查图片清晰度');
      }
    } catch (error) {
      message.error('OCR识别失败');
    } finally {
      setLoading(false);
    }
    return false; // Prevent auto upload
  };

  const columns = [
    {
      title: '指标名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '数值',
      key: 'value',
      render: (_: any, record: MedicalExamIndicator) => (
        <Tag color="blue">{record.value} {record.unit}</Tag>
      )
    },
    {
      title: '参考范围',
      dataIndex: 'referenceRange',
      key: 'referenceRange',
      render: (value: string) => value || '-'
    }
  ];

  return (
    <div>
      <Alert
        message="OCR识别体检报告"
        description="上传体检报告图片，自动识别关键指标。支持常见的血常规、尿常规、生化指标等。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Upload
        fileList={fileList}
        onChange={({ fileList }) => setFileList(fileList)}
        beforeUpload={handleUpload}
        accept="image/*"
        maxCount={1}
      >
        <Button icon={<UploadOutlined />}>选择图片</Button>
      </Upload>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>正在识别...</p>
        </div>
      )}

      {!loading && indicators.length > 0 && (
        <Card title="识别结果" style={{ marginTop: 16 }} size="small">
          <Table
            dataSource={indicators}
            columns={columns}
            rowKey="name"
            pagination={false}
            size="small"
          />
          <Alert
            message="请核对识别结果"
            description="OCR识别可能存在误差，请仔细核对数值是否正确。"
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>
      )}

      {!loading && rawText && indicators.length === 0 && (
        <Card title="原始文本" style={{ marginTop: 16 }} size="small">
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{rawText}</pre>
          <Alert
            message="未能识别出标准指标"
            description="系统未能从文本中提取出标准健康指标。您可以手动录入数据。"
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>
      )}
    </div>
  );
};

export default OCRUpload;
